import { message } from "antd";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import { getEHRTransferStatus, selectTransferAppointment, startEHRTransfer } from "../../service/notes.service";
import type {
  AppointmentOption,
  EHRCredentialCheckResponse,
  EHRTransferStatus,
  EHRTransferStatusResponse,
  StartEHRTransferResponse,
} from "../../types/index.type";

// Constants
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_COUNT = 60; // 5 minutes
const ACTIVE_STATUSES: EHRTransferStatus[] = [
  "pending",
  "logging_in",
  "patient_lookup",
  "awaiting_appointment",
  "template_mapping",
  "writing_note",
];
const CHROME_EXTENSION_URL =
  "https://chromewebstore.google.com/detail/khnjmojbacjmfbplefmcinlmlbifhjnj?utm_source=item-share-cb";

const getUserFriendlyError = (error?: string): string => {
  return error || "Transfer failed. Please try again or contact support.";
};

interface TransferContextValue {
  isLoading: boolean;
  credentialStatus: EHRCredentialCheckResponse | null;
  currentStatus: EHRTransferStatus;
  jobId: string | null;
  appointmentOptions: AppointmentOption[];
  showAppointmentModal: boolean;
  isSelectingAppointment: boolean;
  startTransfer: () => Promise<void>;
  selectAppointment: (appointmentId: string) => Promise<void>;
  cancelAppointment: () => void;
}

const TransferContext = createContext<TransferContextValue | null>(null);

export const useTransfer = () => {
  const context = useContext(TransferContext);
  if (!context) {
    throw new Error("useTransfer must be used within TransferProvider");
  }
  return context;
};

interface TransferProviderProps {
  audioId: number;
  children: React.ReactNode;
}

export const TransferProvider: React.FC<TransferProviderProps> = ({ audioId, children }) => {
  // Get credential status from Redux
  const { userInfo } = useAppSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState<EHRTransferStatus>("not_started");
  const [jobId, setJobId] = useState<string | null>(null);
  const [appointmentOptions, setAppointmentOptions] = useState<AppointmentOption[]>([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [isSelectingAppointment, setIsSelectingAppointment] = useState(false);

  // Derive credentialStatus from Redux (replaces state)
  const credentialStatus: EHRCredentialCheckResponse = {
    has_credential: userInfo.enable_ehr_transfer ?? false,
  };

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shownModalForJobRef = useRef<string | null>(null);

  // Handle status updates (used by both initial check and polling)
  const handleStatusUpdate = useCallback(
    (statusData: EHRTransferStatusResponse) => {
      if (!statusData.job) return;

      const { job } = statusData;
      setJobId(job.id);
      setCurrentStatus(job.status);

      // Handle appointment selection modal (only show once per job)
      if (
        job.status === "awaiting_appointment" &&
        job.requires_appointment_selection &&
        job.appointment_options &&
        shownModalForJobRef.current !== job.id
      ) {
        setAppointmentOptions(job.appointment_options);
        setShowAppointmentModal(true);
        shownModalForJobRef.current = job.id;
        return;
      }

      // Show notifications for terminal states (only once)
      if (job.status === "completed" && currentStatus !== "completed") {
        message.success("Note transferred to EHR successfully!");
      } else if (job.status === "failed" && currentStatus !== "failed") {
        message.error(getUserFriendlyError(job.error_message));
      }
    },
    [currentStatus],
  );

  // Initialize: Check transfer status
  useEffect(() => {
    const initialize = async () => {
      try {
        // Only fetch transfer status (credential status now from Redux)
        const statusRes = await getEHRTransferStatus(audioId);
        handleStatusUpdate(statusRes.data);
      } catch (error) {
        console.error("Error initializing transfer:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [audioId, handleStatusUpdate]);

  // Polling: Poll status when transfer is active
  useEffect(() => {
    const shouldPoll = ACTIVE_STATUSES.includes(currentStatus);

    if (!shouldPoll) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    let pollCount = 0;

    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;

      try {
        const res = await getEHRTransferStatus(audioId);
        handleStatusUpdate(res.data);

        // Stop polling on terminal states
        if (res.data.job && !ACTIVE_STATUSES.includes(res.data.job.status)) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error("Error polling transfer status:", error);
      }

      // Timeout after max polls
      if (pollCount >= MAX_POLL_COUNT) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        message.warning("Transfer is taking longer than expected. Please check back later.");
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [audioId, currentStatus, handleStatusUpdate]);

  // Start transfer
  const startTransfer = useCallback(async () => {
    if (!audioId) {
      message.error("Invalid note ID");
      return;
    }

    // If no credentials, redirect to Chrome extension
    if (!credentialStatus?.has_credential) {
      window.open(CHROME_EXTENSION_URL, "_blank");
      return;
    }

    setIsLoading(true);

    try {
      const res = await startEHRTransfer(audioId);
      const data: StartEHRTransferResponse = res.data;

      if (data.success) {
        message.info("Transferring note to EHR...");
        setCurrentStatus(data.status || "pending");
        setJobId(data.job_id || null);
      } else {
        handleTransferError(data);
      }
    } catch (error) {
      console.error("Error starting EHR transfer:", error);
      const axiosError = error as { response?: { data?: StartEHRTransferResponse } };
      handleTransferError(axiosError.response?.data);
    } finally {
      setIsLoading(false);
    }
  }, [audioId, credentialStatus]);

  // Handle transfer errors
  const handleTransferError = (data?: StartEHRTransferResponse) => {
    const errorCode = data?.code;

    switch (errorCode) {
      case "no_credential":
        window.open(CHROME_EXTENSION_URL, "_blank");
        break;
      case "already_transferred":
        message.info("This note was already transferred");
        break;
      case "in_progress":
        message.info("Transfer is already in progress");
        setCurrentStatus(data?.status || "pending");
        setJobId(data?.job_id || null);
        break;
      case "no_client":
        message.error("Note has no associated client/patient");
        break;
      case "missing_patient_name":
      case "missing_patient_dob":
        message.error("Patient information is incomplete in client record");
        break;
      default:
        message.error(getUserFriendlyError(data?.error));
    }
  };

  // Select appointment
  const selectAppointment = useCallback(
    async (appointmentId: string) => {
      if (!jobId) {
        message.error("No active transfer job");
        return;
      }

      setIsSelectingAppointment(true);

      try {
        const response = await selectTransferAppointment(jobId, appointmentId);
        setShowAppointmentModal(false);

        const responseStatus = response?.data?.status;
        if (responseStatus === "completed") {
          setCurrentStatus("completed");
          message.success("Note transferred to EHR successfully!");
        } else if (responseStatus === "failed") {
          setCurrentStatus("failed");
          message.error(getUserFriendlyError(response?.data?.error_message));
        } else {
          // Force status to template_mapping to start polling
          setCurrentStatus("template_mapping");
          message.info("Appointment selected, continuing transfer...");
        }
      } catch (error) {
        console.error("Error selecting appointment:", error);
        message.error("Failed to select appointment");
      } finally {
        setIsSelectingAppointment(false);
      }
    },
    [jobId],
  );

  // Cancel appointment modal (just close it, don't change status)
  const cancelAppointment = useCallback(() => {
    setShowAppointmentModal(false);
    // Don't set status to "failed" - the job is still running in background
    // Polling will update to the real status
  }, []);

  const value: TransferContextValue = {
    isLoading,
    credentialStatus,
    currentStatus,
    jobId,
    appointmentOptions,
    showAppointmentModal,
    isSelectingAppointment,
    startTransfer,
    selectAppointment,
    cancelAppointment,
  };

  return <TransferContext.Provider value={value}>{children}</TransferContext.Provider>;
};
